// src/services/authService.js
// Servicio centralizado de autenticación (Supabase + Backend propio)

import { supabase } from "../libs/supabase";

// src/services/authService.js
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://rkfsuwvgiigihkeatsbq.supabase.co";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

const AUTH_STORAGE_KEY = "auth";

// ----------------- Helpers de storage -----------------
function saveAuthSession(session) {
  localStorage.setItem(
    AUTH_STORAGE_KEY,
    JSON.stringify({
      ...session,
      saved_at: Date.now(),
    })
  );
}
function readAuthSession() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function clearAuthSession() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}
function isTokenExpired(expires_at) {
  if (!expires_at) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return nowSec >= expires_at;
}
function getAuthHeader() {
  const auth = readAuthSession();
  if (!auth?.access_token) return {};
  return { Authorization: `Bearer ${auth.access_token}` };
}

// src/services/authService.js (fragmento de login)
// src/services/authService.js
async function loginWithSupabase({ email, password }) {
  // trims defensivos (copiar/pegar suele traer espacios)
  const _email = String(email || "").trim();
  const _password = String(password || "").trim();

  const endpoint = `${SUPABASE_URL}/auth/v1/token?grant_type=password`;

  // Depuración: asegúrate de que el proyecto y clave sean los correctos
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error("Faltan REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY en el .env del front");
  }
  // (opc) mira en consola los 8 primeros chars para validar que coinciden con Postman
  console.debug("[auth] SUPABASE_URL:", SUPABASE_URL);
  console.debug("[auth] ANON prefix:", SUPABASE_ANON_KEY.slice(0, 8) + "...");

  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`, // tal como en tu Postman
    },
    body: JSON.stringify({
      email: _email,
      password: _password,
      gotrue_meta_security: {},
    }),
  });

  // Lee texto bruto para ver exactamente qué responde GoTrue
  const raw = await res.text();
  let data = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch { data = { raw }; }

  if (!res.ok) {
    console.warn("[auth] status:", res.status, "body:", data);
    // Supabase suele enviar error_description/error/msg
    const msg =
      data?.error_description ||
      data?.error ||
      data?.msg ||
      data?.raw ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.payload = data;
    throw err;
  }

  const {
    access_token,
    refresh_token,
    token_type,
    expires_in,
    user,
    expires_at,
  } = data;

  const computedExpiresAt =
    expires_at || Math.floor(Date.now() / 1000) + (expires_in || 3600);

  saveAuthSession({
    access_token,
    refresh_token,
    token_type,
    user,
    expires_at: computedExpiresAt,
  });

  return { user, access_token };
}


// ----------------- Backend: registro -----------------
async function registerViaBackend(payload) {
  // POST http://localhost:3001/api/v1/auth/register
  const res = await fetch(`${BACKEND_URL}/api/v1/auth/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeader(), // si tu endpoint exige auth, quedará listo
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (!res.ok) {
    const msg = data?.message || data?.error || "No se pudo registrar el usuario";
    const err = new Error(msg);
    err.payload = data;
    throw err;
  }

  return data;
}

// ----------------- Utilidades públicas -----------------
function getCurrentUser() {
  const auth = readAuthSession();
  return auth?.user || null;
}

function logout() {
  clearAuthSession();
}

async function resetPasswordWithToken(token, newPassword) {
  try {

    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    }, {
      accessToken: token
    })

    if (error) {
      return { ok: false, message: error.message }
    }
    return { ok: true, message: "Contraseña actualizada correctamente", data }
  } catch (err) {
    console.error("Error inesperado:", err)
    return { error: err }
  }
}

async function sendEmail(email) {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password'
    })
    if (error) {
      if(error.message === "User not found") {
        return { ok: false, message: "No se encontró un usuario con ese correo electrónico." }
      }else if(error.message === "Email not confirmed") {
        return { ok: false, message: "El correo electrónico no ha sido confirmado. Revisa tu bandeja de entrada." }
      }else if(error.message.startsWith("For security purposes, you can only request")) {
        return { ok: false, message: "Se han realizado demasiados intentos en poco tiempo. Por favor, intenta más tarde." }
      }
      return { ok: false, message: error.message }
    }
    return { ok: true, message: "Correo de recuperación enviado", data }
  } catch (error) {
    return { ok: false, error: error }
  }
}

export {
  // Supabase
  loginWithSupabase,
  // Backend
  registerViaBackend,
  // Session utils
  getCurrentUser,
  getAuthHeader,
  isTokenExpired,
  logout,
  readAuthSession,
  saveAuthSession,
  clearAuthSession,
  resetPasswordWithToken,
  sendEmail,
  // Consts (por si los necesitas)
  BACKEND_URL,
  SUPABASE_URL,
};
